import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { normalizeCompanyContext } from '../utils/companyContract';
import { companiesAPI } from '../services/api';
import { deleteByCompanyCin as cacheDeleteByCompanyCin } from '../services/draftCacheService';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);
const ACTIVE_COMPANY_STORAGE_KEY = 'cs_active_company_cin';

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

/**
 * Stored in place of a CIN when the user has chosen to work with NO company —
 * general questions to Vee, nothing company-scoped. Distinct from '' (nothing
 * chosen yet), which the effect below fills with the first company; this one
 * it leaves alone. (Ritik, 21 Sep 2026: "give me an option to work on Vee
 * without selecting any company".)
 */
export const NO_COMPANY = '__none__';

export const AppProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeCompanyCin, setActiveCompanyCin] = useState(() => {
    try {
      return localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);
  // Guards the active-company fallback effect: the companies list starts empty
  // on every mount and loads async, so we must NOT treat that initial [] as
  // "user has no companies" and wipe the persisted selection. Only true once a
  // fetch has actually completed.
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  // When set, the global company modal opens pre-filled in view/edit mode for
  // this company (e.g. picked from spotlight search). null → blank "add" mode.
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const saveCompanyRequestsRef = useRef(new Map());

  const noCompanyMode = activeCompanyCin === NO_COMPANY;
  const activeCompany = useMemo(() => {
    if (!activeCompanyCin || activeCompanyCin === NO_COMPANY) return null;
    return companies.find((company) => String(company.cin || '').toUpperCase() === activeCompanyCin) || null;
  }, [activeCompanyCin, companies]);

  useEffect(() => {
    // Wait for the first fetch to complete — otherwise the initial empty list
    // would clear the persisted CIN before companies arrive, resetting the
    // active company to companies[0] on every reload.
    if (!companiesLoaded) return;

    if (!companies.length) {
      setActiveCompanyCin('');
      return;
    }

    if (activeCompany) return;
    // Chosen on purpose: not a dead pointer, so do not fall back to a company.
    if (activeCompanyCin === NO_COMPANY) return;

    // The pinned CIN does not resolve against the portfolio we just fetched:
    // the company was deleted, claimed away, our access to it lapsed, or it was
    // pinned optimistically after a create that never landed. NEVER keep a dead
    // pointer — it is what the AI chat sends as activeCompanyCin, and every
    // company-scoped tool call made against it is refused server-side. Fall
    // back to the first accessible company, or clear (a company row with no
    // CIN can't be a fallback, and '' is still better than a dead pointer).
    setActiveCompanyCin(String(companies[0]?.cin || '').trim().toUpperCase());
    // activeCompanyCin is a dependency so that a CIN pinned directly (e.g.
    // selectCompany({ cin }) right after add_company) is re-checked against the
    // list even when activeCompany was already null before it.
  }, [activeCompany, activeCompanyCin, companies, companiesLoaded]);

  useEffect(() => {
    try {
      if (activeCompanyCin) {
        localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, activeCompanyCin);
      } else {
        localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures
    }
  }, [activeCompanyCin]);

  const selectCompany = useCallback((company) => {
    const cin = String(company?.cin || '').trim().toUpperCase();
    if (!cin) return;
    setActiveCompanyCin(cin);
  }, []);

  /** Work with no company: general questions, nothing company-scoped. Sticks across reloads. */
  const clearCompany = useCallback(() => {
    setActiveCompanyCin(NO_COMPANY);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  // Fetch companies from API
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await companiesAPI.getAll();

      if (response.success) {
        // normalizeCompanyContext is the AI-pipeline contract and deliberately
        // omits access metadata. Re-attach owner/createdBy/claimedAt so the
        // portfolio can show the Owner / Claimed / Shared relationship badge.
        const normalizedCompanies = response.data.map((c) => ({
          ...normalizeCompanyContext(c),
          owner: c.owner ?? null,
          createdBy: c.createdBy ?? null,
          claimedAt: c.claimedAt ?? null,
          myAccessLevel: c.myAccessLevel ?? null,
        }));
        setCompanies(normalizedCompanies);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
      setCompaniesLoaded(true);
    }
  }, []);

  // Load companies when user is authenticated and auth loading is complete
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCompanies();
    } else if (!isAuthenticated && !authLoading) {
      // Clear companies when user is not authenticated. Reset the loaded flag so
      // a fresh login re-gates the fallback effect (and keeps the persisted CIN
      // intact for when they sign back in).
      setCompanies([]);
      setCompaniesLoaded(false);
    }
  }, [isAuthenticated, authLoading, fetchCompanies]);

  const addOrUpdateCompany = useCallback(async (company) => {
    const cin = String(company?.cin || '').trim().toUpperCase();
    if (!cin) throw new Error('CIN is required');

    const inFlightRequest = saveCompanyRequestsRef.current.get(cin);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const request = (async () => {
      try {
        // Normalize incoming company through the canonical contract
        const normalizedCompany = normalizeCompanyContext({ ...company, cin });

        // Check if company already exists
        const existingCompany = companies.find(c =>
          String(c.cin || '').toUpperCase() === cin
        );

        let response;
        if (existingCompany) {
          // Update existing company
          const companyId = existingCompany._id || existingCompany.id;
          if (!companyId) {
            throw new Error('Company ID not found for update operation');
          }
          response = await companiesAPI.update(companyId, normalizedCompany);
        } else {
          // Create new company
          response = await companiesAPI.create(normalizedCompany);
        }

        if (response.success) {
          // Refresh companies list
          await fetchCompanies();
          // `outcome` (created | claimed | access_requested | already_has_access)
          // lets callers tailor their feedback. Defaults to 'created' for the
          // plain update path which doesn't return one.
          return { data: response.data, outcome: response.outcome || 'created' };
        }

        throw new Error(response.message || 'Failed to save company');
      } catch (error) {
        console.error('Error saving company:', error);
        throw error;
      }
    })();

    saveCompanyRequestsRef.current.set(cin, request);
    try {
      return await request;
    } finally {
      saveCompanyRequestsRef.current.delete(cin);
    }
  }, [companies, fetchCompanies]);

  const deleteCompany = useCallback(async (companyId) => {
    try {
      // Capture the CIN before the row is gone from state — we need it
      // after the API call succeeds to evict the IndexedDB cache rows that
      // would otherwise keep resurfacing this company's drafts.
      const target = companies.find((c) => c._id === companyId || c.id === companyId);
      const cinToEvict = target?.cin || null;

      const response = await companiesAPI.delete(companyId);

      if (response.success) {
        // Stale-cache hygiene: any IndexedDB draft tied to this company is
        // now orphaned. Wipe those rows so re-opening a template won't pull
        // the deleted company's body back from cache. Fire-and-forget —
        // failures here are non-fatal (cache is best-effort).
        if (cinToEvict) {
          cacheDeleteByCompanyCin(cinToEvict).catch(() => {});
        }
        // Refresh companies list
        await fetchCompanies();
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }, [companies, fetchCompanies]);

  const openAddCompanyModal = useCallback(() => {
    setCompanyToEdit(null);
    setIsAddCompanyModalOpen(true);
  }, []);

  // Open the same modal pre-filled to view/edit an existing company.
  const openCompanyDetails = useCallback((company) => {
    setCompanyToEdit(company || null);
    setIsAddCompanyModalOpen(true);
  }, []);

  const closeAddCompanyModal = useCallback(() => {
    setIsAddCompanyModalOpen(false);
    setCompanyToEdit(null);
  }, []);

  // Memoized so consumers don't re-render on every provider render.
  const value = useMemo(() => ({
    activeCompany,
    // The persisted CIN, available SYNCHRONOUSLY on first render (from
    // localStorage) — whereas `activeCompany` stays null until the companies
    // API responds and the memo above can resolve it. Surfaces that only need
    // the identity of the active company (e.g. keying a cache or a query by
    // CIN) should read this so they aren't blocked on that round-trip.
    activeCompanyCin,
    selectCompany,
    clearCompany,
    sidebarCollapsed,
    toggleSidebar,
    companies,
    loading,
    addOrUpdateCompany,
    deleteCompany,
    fetchCompanies,
    isAddCompanyModalOpen,
    openAddCompanyModal,
    openCompanyDetails,
    companyToEdit,
    closeAddCompanyModal,
  }), [
    activeCompany, activeCompanyCin, noCompanyMode, selectCompany, clearCompany, sidebarCollapsed, toggleSidebar,
    companies, loading, addOrUpdateCompany, deleteCompany, fetchCompanies,
    isAddCompanyModalOpen, openAddCompanyModal, openCompanyDetails, companyToEdit, closeAddCompanyModal,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
